/**
 * Gatsby – Browser API
 * Keeps MUI v5 + styled-components style order happy
 *
 * DSG warm-up strategy
 *  First-hover on a link and  First time link enters viewport (___gatsbyPrefetch())
 *  DOMContentLoaded (HEAD ping) - wake Netlify builder
 *  Main-thread idle to trickle-prefetch every link
 */

import React from "react"
import { ThemeProvider as ScThemeProvider } from "styled-components"
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles"

import { defaultTheme } from "./src/lib/styled-system/constants"
import { muiTheme } from "./src/theme/muiTheme"

// one wrapper to ensure MUI + styled‑components share the same <style> tag
export const wrapRootElement = ({ element }) => (
  <StyledEngineProvider injectFirst>
    <MuiThemeProvider theme={muiTheme}>
      <ScThemeProvider theme={defaultTheme}>{element}</ScThemeProvider>
    </MuiThemeProvider>
  </StyledEngineProvider>
)

/* ─────────────────── DSG helpers ─────────────────── */
const markPrefetched = link => (link.dataset.prefetched = "true")

/* hover */
const hoverPrefetch = link => {
  markPrefetched(link)
  link.addEventListener("mouseenter", () => {
    const href = link.getAttribute("href")
    if (href) window.___gatsbyPrefetch?.(href)
  })
}

/* viewport */
const viewportPrefetch = links => {
  if (!("IntersectionObserver" in window)) return
  const io = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const href = entry.target.getAttribute("href")
          if (href) window.___gatsbyPrefetch?.(href)
          io.unobserve(entry.target)
        }
      })
    },
    { rootMargin: "0px 0px 400px 0px" }, // trigger a bit sooner
  )
  links.forEach(l => io.observe(l))
}

/* idle-time full sweep  */
const idleSweepPrefetch = () => {
  if (!window.___gatsbyPrefetch) return

  const schedule =
    "requestIdleCallback" in window
      ? cb => window.requestIdleCallback(cb, { timeout: 8_000 })
      : cb => setTimeout(cb, 1_500)

  schedule(() => {
    const queue = [
      ...document.querySelectorAll(
        "a[data-dsg-prefetch]:not([data-prefetched])",
      ),
    ]
    const conn = navigator.connection || navigator.mozConnection
    if (conn && (conn.saveData || /(2g)/.test(conn.effectiveType))) return

    const pump = () => {
      const link = queue.shift()
      if (!link) return
      const href = link.getAttribute("href")
      if (href) window.___gatsbyPrefetch(href)
      markPrefetched(link)
      schedule(pump)
    }
    pump()
  })
}

/* HEAD ping – fire builder ASAP (runs once per route) */
if (typeof window !== "undefined") {
  window.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("a[data-dsg-prefetch]").forEach(a =>
      /* HEAD avoids downloading full HTML but still wakes the builder */
      fetch(a.href, { method: "HEAD", mode: "no-cors" }).catch(() => {}),
    )
  })
}

/* ─────────────────── route hook ─────────────────── */
export const onRouteUpdate = () => {
  if (typeof document === "undefined" || !window.___gatsbyPrefetch) return

  const fresh = [
    ...document.querySelectorAll("a[data-dsg-prefetch]:not([data-prefetched])"),
  ]
  if (!fresh.length) return

  fresh.forEach(hoverPrefetch)
  viewportPrefetch(fresh)
  idleSweepPrefetch()
}
